import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import SlashCommand from "@/components/SlashCommand";
import "tippy.js/dist/tippy.css";
import { useEffect, useMemo } from "react";
import DOMPurify from "dompurify";
import {
  Bold, Italic, List, ListOrdered, Quote, Code, Heading2, Heading3,
  Link as LinkIcon, Strikethrough, Undo2, Redo2,
} from "lucide-react";

export default function RichTextEditor({ value, onChange, placeholder = "Start typing… (try `/` for blocks)", maxHeight }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false }),
      Link.configure({ openOnClick: true, autolink: true, HTMLAttributes: { class: "text-[#0a2540] underline" } }),
      Placeholder.configure({ placeholder }),
      TaskList,
      TaskItem.configure({ nested: true }),
      SlashCommand,
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-[180px] px-3 py-3 leading-relaxed",
        "data-testid": "rt-editor-content",
      },
    },
    onUpdate: ({ editor }) => { onChange?.(editor.getHTML()); },
  });

  // Sync external value changes (e.g., on editing existing page)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== undefined && value !== current) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url, target: "_blank" }).run();
  };

  const Btn = ({ on, active, label, icon: Icon, testid }) => (
    <button
      type="button"
      onClick={on}
      title={label}
      className={`h-7 w-7 inline-flex items-center justify-center text-xs rounded-sm ${
        active ? "bg-[#0a2540] text-white" : "text-slate-600 hover:bg-slate-100"
      }`}
      data-testid={testid}
    >
      <Icon size={13} />
    </button>
  );

  return (
    <div className="border border-slate-200 rounded-sm overflow-hidden bg-white" data-testid="rich-text-editor">
      <div className="flex items-center gap-0.5 px-1.5 py-1 border-b border-slate-200 bg-slate-50 flex-wrap">
        <Btn on={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} label="Bold" icon={Bold} testid="rt-bold" />
        <Btn on={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} label="Italic" icon={Italic} testid="rt-italic" />
        <Btn on={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} label="Strike" icon={Strikethrough} testid="rt-strike" />
        <div className="w-px h-4 bg-slate-300 mx-1" />
        <Btn on={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} label="H2" icon={Heading2} testid="rt-h2" />
        <Btn on={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} label="H3" icon={Heading3} testid="rt-h3" />
        <div className="w-px h-4 bg-slate-300 mx-1" />
        <Btn on={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} label="Bullets" icon={List} testid="rt-bullet" />
        <Btn on={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} label="Numbered" icon={ListOrdered} testid="rt-ordered" />
        <Btn on={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} label="Quote" icon={Quote} testid="rt-quote" />
        <Btn on={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} label="Code" icon={Code} testid="rt-code" />
        <div className="w-px h-4 bg-slate-300 mx-1" />
        <Btn on={setLink} active={editor.isActive("link")} label="Link" icon={LinkIcon} testid="rt-link" />
        <div className="w-px h-4 bg-slate-300 mx-1" />
        <Btn on={() => editor.chain().focus().undo().run()} label="Undo" icon={Undo2} testid="rt-undo" />
        <Btn on={() => editor.chain().focus().redo().run()} label="Redo" icon={Redo2} testid="rt-redo" />
      </div>
      <div
        className={maxHeight ? "overflow-y-auto" : ""}
        style={maxHeight ? { maxHeight } : undefined}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

export function RichTextView({ html }) {
  const sanitized = useMemo(
    () => (html ? DOMPurify.sanitize(html, { USE_PROFILES: { html: true } }) : ""),
    [html],
  );
  if (!html) return <div className="text-xs text-slate-400">—</div>;
  return (
    <div
      className="prose prose-sm max-w-none text-slate-700 leading-relaxed"
      dangerouslySetInnerHTML={{ __html: sanitized }}
      data-testid="rich-text-view"
    />
  );
}
